import React, { useEffect, useState } from 'react';
import { Button, FormControl, FormHelperText, LinearProgress, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { convertCSVToRows, convertRowsToCSV, createInitialTags, EMPTY_ROW, KeyphraseRow, validateRow } from '../../../../utils/keyphrases';
import { FieldState, UploadStatus } from '../../../../types/generic';
import { keyphrasesInitialState } from '../../../../utils/initialStates';
import { addError, checkRequired, getHelperText, handleFieldValueUpdate } from '../../../../utils/form';
import FormTextField from '../../../Form/FormTextField';
import KeyphraseTable from './KeyphrasesTable';
import KeyphraseHelpText from './KeyphrasesHelpText';
import { callAPISync, getAllKeyphraseFiles, getAllOrganisations, registerKeyphraseFile } from '../../../../utils/api';
import { sha256 } from 'js-sha256';
import { checkFileExists, downloadAzureFile, uploadAzureFile, validateBlobName } from '../../../../utils/azure';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import SubmissionAlert from '../../../SubmissionAlert';
import { KeyphrasesInfo } from '../../../../types/upload';
import Logger from '../../../../utils/logger';
import AlertDialog from '../../../AlertDialog';
import { resetFileUpload, updateStatus } from '../../../../store/upload/uploadSlice';
import LabelledSelect from '../../../LabelledSelect';
import { Organisation } from '../../../../types/user';
import { useLogto } from '@logto/react';

const CreateKeyphrases: React.FC = () => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const userOrgs = useAppSelector((state) => state.user.orgs);
	const userRoles = useAppSelector((state) => state.tab.userRoles);

	const [fieldValues, setFieldValues] = useState<FieldState>({ ...keyphrasesInitialState });
	const [rows, updateRows] = useState<KeyphraseRow[]>([{ ...EMPTY_ROW }]);
	const [tagOptions, updateTagOptions] = useState<string[]>(createInitialTags(rows));
	const [errorRows, setErrorRows] = useState<number[]>([]);
	const [uploadStatus, setUploadStatus] = useState<string>('');
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [uploadError, setUploadError] = useState<string>('');
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const [keyphraseOptions, setKeyphraseOptions] = useState<KeyphrasesInfo[]>([]);
	const [keyphraseError, setKeyphraseError] = useState<string>('');
	const [loadingKeyphrase, setLoadingKeyphrase] = useState<boolean>(false);
	const [queuedExtendList, setQueuedExtendList] = useState<KeyphrasesInfo | null>(null);
	const [displayedOrgs, setDisplayedOrgs] = useState<Organisation[]>(userOrgs);

	const extendDialogOpen = Boolean(menuAnchor);
	const changeInProgress = loadingKeyphrase || (Boolean(uploadStatus) && uploadProgress < 100);

	useEffect(() => {
		callAPISync(logto, (token) => {
			getAllKeyphraseFiles(token)
				.then((res) => res.json())
				.then((resp: KeyphrasesInfo[]) => setKeyphraseOptions(resp))
				.catch((e) => {
					setKeyphraseError(`An error occurred when fetching existing keyphrases: (${String(e)})`);
					Logger.error(e);
				});
		});
	}, []);

	useEffect(() => {
		// Devs / Admins can create a list for any organisation
		if (userRoles.includes('DEVELOPER') || userRoles.includes('ADMINISTRATOR')) {
			callAPISync(logto, (token) => {
				getAllOrganisations(token)
					.then((res) => res.json())
					.then((resp: Organisation[]) => {
						if (resp.length === 1) {
							setFieldValues({
								...fieldValues,
								organisation: {
									...fieldValues.organisation,
									value: resp[0].id,
									error: false,
								},
							});
						}
						setDisplayedOrgs(
							resp.map((org) => {
								return {
									name: org.name,
									id: org.id,
									permissions: [],
									roles: [],
								};
							})
						);
					})
					.catch((e) => {
						Logger.error(e);
					});
			});
		}
	}, []);

	const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		dispatch(
			updateStatus({
				newStatus: UploadStatus.UPLOADING,
				uploadType: 'Keyphrase File Creation',
				warningMessage: 'You have unsaved progress on this page',
			})
		);
		setUploadStatus('');
		setUploadProgress(0);
		handleFieldValueUpdate(name, value, fieldValues, setFieldValues);
	};

	const handleSubmitError = (e: unknown) => {
		setUploadStatus('');
		setUploadError(String(e));
	};

	const onSubmit = () => {
		const newRows = [...rows];
		const newVals = { ...fieldValues };

		setUploadError('');
		setUploadProgress(0);
		setUploadStatus('');
		dispatch(resetFileUpload());

		checkRequired(newVals);
		const nameErrs = validateBlobName(newVals.title.value.toString());
		if (nameErrs) {
			addError(fieldValues.title, nameErrs);
		}

		newRows.forEach((row) => {
			row.errorFields = validateRow(row);
		});

		const fieldErrs = Object.values(fieldValues).filter((val) => val.error === true).length > 0;
		const rowErrs = newRows.some((row) => row.errorFields && Object.keys(row.errorFields).length > 0);

		const errs: number[] = [];
		if (rowErrs) {
			newRows.forEach((row, idx) => {
				if (row.errorFields && Object.keys(row.errorFields).length > 0) {
					errs.push(idx + 1);
				}
			});
			updateRows(newRows);
		}

		if (!rowErrs && !fieldErrs) {
			setUploadStatus('Generating Keyphrases List CSV');

			const fileName = `${fieldValues.title.value.toString().toLocaleLowerCase()}.csv`;
			const csv = convertRowsToCSV(rows);
			const csvFile = new File([new Blob([csv], { type: 'text/csv' })], fileName);

			setUploadStatus(`Uploading ${fileName} to Azure`);
			setUploadProgress(10);

			callAPISync(logto, (token) => {
				checkFileExists('keywords', fileName, token)
					.then((exists) => {
						if (exists) {
							setUploadError(`A Keyphrases List already exists with the filename "${fileName}"`);
							setUploadProgress(0);
							setUploadStatus('');
						} else {
							uploadAzureFile('keywords', csvFile, token)
								.then(() => {
									setUploadStatus(`Adding ${fileName} to selectable Keyphrases Lists`);
									setUploadProgress(60);

									const organisation =
										fieldValues.organisation.value.toString() === '*allOrgs*' ? undefined : fieldValues.organisation.value.toString();

									registerKeyphraseFile(
										token,
										fieldValues.pipeline.value.toString(),
										fileName,
										fieldValues.title.value.toString(),
										fieldValues.useCase.value.toString(),
										sha256(csv),
										'gui',
										organisation
									)
										.then((res) => {
											if (res.status === 200) {
												setUploadStatus('Keyphrases creation complete!');
												setUploadProgress(100);
												setFieldValues({ ...keyphrasesInitialState });
												updateRows([{ ...EMPTY_ROW }]);
												window.scrollTo(0, 0);
											} else {
												setUploadStatus('');
												setUploadError(`Unhandled error registering Keyphrase List: ${res.statusText}`);
											}
										})
										.catch(handleSubmitError);
								})
								.catch(handleSubmitError);
						}
					})
					.catch(handleSubmitError);
			});
		}

		setFieldValues({
			...newVals,
		});
		setErrorRows(errs);
	};

	const getLoadingBarColour = (): 'error' | 'success' | 'primary' => {
		if (uploadError) {
			return 'error';
		} else if (uploadProgress > 99) {
			return 'success';
		} else {
			return 'primary';
		}
	};

	const getKeyphraseMenuOptions = () => {
		if (keyphraseOptions.length === 0 && keyphraseError === '') {
			return <MenuItem disabled>Loading...</MenuItem>;
		} else if (keyphraseError) {
			return (
				<MenuItem disabled color="error">
					{keyphraseError}
				</MenuItem>
			);
		} else {
			return (
				<>
					{keyphraseOptions
						//only CSVs are supported
						.filter((opt) => opt.location.endsWith('.csv'))
						.map((opt) => {
							return (
								<MenuItem
									key={`opt-${opt.title}`}
									value={opt.title}
									onClick={() => {
										setQueuedExtendList(opt);
									}}
									data-testid={`keyphrase-opt-${opt.id}`}
								>
									<ListItemText
										primary={opt.title}
										secondary={
											<>
												<Typography paddingLeft={1} variant="body2" color="text.secondary" component={'span'}>
													Use Case: {opt.useCase}
												</Typography>
												<br />
												<Typography paddingLeft={1} variant="body2" color="text.secondary" component={'span'}>
													Last Modified: {opt.lastModified}
												</Typography>
											</>
										}
									/>
								</MenuItem>
							);
						})}
				</>
			);
		}
	};

	const loadExistingKeyphraseList = (chosenKeyphrases: KeyphrasesInfo) => {
		// set known values from the chosen item (except title)
		const toBeChanged: FieldState = {};
		if (chosenKeyphrases.pipeline !== fieldValues.pipeline.value) {
			toBeChanged['pipeline'] = {
				...fieldValues.pipeline,
				value: chosenKeyphrases.pipeline,
				error: false,
			};
		}

		if (chosenKeyphrases.useCase !== fieldValues.useCase.value) {
			toBeChanged['useCase'] = {
				...fieldValues.useCase,
				value: chosenKeyphrases.useCase,
				error: false,
			};
		}

		if (chosenKeyphrases.organisation && displayedOrgs.length > 1 && chosenKeyphrases.organisation !== fieldValues.organisation.value) {
			toBeChanged['organisation'] = {
				...fieldValues.organisation,
				value: chosenKeyphrases.organisation,
				error: false,
			};
		}

		callAPISync(logto, (token) => {
			downloadAzureFile(chosenKeyphrases.container, chosenKeyphrases.location, token)
				.then(async (blob) => {
					const newRows = convertCSVToRows(await blob.text());

					updateRows(newRows);
					updateTagOptions(createInitialTags(newRows));
					setFieldValues({
						...fieldValues,
						...toBeChanged,
					});
					setLoadingKeyphrase(false);
					setUploadStatus(`Successfully loaded "${chosenKeyphrases.title}" with ${newRows.length} rows`);
					setUploadProgress(100);
				})
				.catch((e) => {
					setUploadError(`Could not fetch existing keywords list ${chosenKeyphrases.title}: ${String(e)}`);
					setLoadingKeyphrase(false);
				});
		});
	};

	return (
		<Stack width="100%" spacing={2}>
			<SubmissionAlert
				showCondition={Boolean(uploadStatus) && uploadProgress > 99}
				success={true}
				onClose={() => {
					setUploadStatus('');
					setUploadProgress(0);
				}}
				messages={[uploadStatus]}
			/>

			<KeyphraseHelpText />

			<Button
				variant="outlined"
				fullWidth
				disabled={changeInProgress}
				data-testid="extend-keyphrases-button"
				onClick={(e) => setMenuAnchor(e.currentTarget)}
			>
				Extend Existing Keyphrases List
			</Button>
			<Menu
				open={extendDialogOpen}
				anchorEl={menuAnchor}
				slotProps={{ paper: { sx: { width: menuAnchor?.offsetWidth } } }}
				onClose={() => setMenuAnchor(null)}
				disableScrollLock
			>
				{getKeyphraseMenuOptions()}
			</Menu>

			<FormTextField
				fieldData={fieldValues.title}
				textFieldProps={{
					label: 'Title',
					name: 'title',
					onChange: handleFieldChange,
					disabled: changeInProgress,
				}}
			/>

			<FormControl fullWidth variant="outlined" disabled={changeInProgress}>
				<LabelledSelect
					selectLabel="Pipeline"
					testId="pipeline-selector"
					value={fieldValues.pipeline.value}
					onChange={(e) =>
						setFieldValues({
							...fieldValues,
							pipeline: {
								...fieldValues.pipeline,
								value: e.target.value,
								error: false,
							},
						})
					}
					required={fieldValues.pipeline.required}
					error={fieldValues.pipeline.error}
					fullWidth
				>
					<MenuItem value="cellebrite-xml">Cellebrite XML</MenuItem>
				</LabelledSelect>

				<FormHelperText>{getHelperText(fieldValues.pipeline)}</FormHelperText>
			</FormControl>

			<FormTextField
				fieldData={fieldValues.useCase}
				textFieldProps={{
					label: 'Use Case',
					name: 'useCase',
					onChange: handleFieldChange,
					disabled: changeInProgress,
				}}
			/>

			<FormControl fullWidth variant="outlined" disabled={changeInProgress}>
				<LabelledSelect
					testId="organisation-selector"
					selectLabel="Organisation"
					value={fieldValues.organisation.value}
					onChange={(e) =>
						setFieldValues({
							...fieldValues,
							organisation: {
								...fieldValues.organisation,
								value: e.target.value,
								error: false,
							},
						})
					}
					required={fieldValues.organisation.required}
					error={fieldValues.organisation.error}
					fullWidth
				>
					<MenuItem key={'allOrgs'} value={'*allOrgs*'} data-testid="all-orgs-option">
						{'All Organisations'}
					</MenuItem>
					{displayedOrgs.map((org) => (
						<MenuItem key={org.id} value={org.id}>
							{org.name}
						</MenuItem>
					))}
				</LabelledSelect>
				<FormHelperText>{getHelperText(fieldValues.organisation)}</FormHelperText>
			</FormControl>

			{/* the key resets the state when a change is made so it returns to the first page */}
			<KeyphraseTable
				key={changeInProgress ? 'change' : 'noChange'}
				rows={rows}
				updateRows={updateRows}
				tagOptions={tagOptions}
				updateTagOptions={updateTagOptions}
				errorRows={errorRows}
				changeInProgress={changeInProgress}
			/>

			<Button variant="contained" fullWidth onClick={onSubmit} disabled={changeInProgress} data-testid="keyphrases-submit-button">
				{changeInProgress ? uploadStatus : 'Submit'}
			</Button>

			{(uploadStatus || uploadError) && (
				<LinearProgress
					variant="determinate"
					value={uploadProgress}
					color={getLoadingBarColour()}
					data-testid="keyphrases-progress-bar"
					aria-label="Keyword Submission Progress Bar"
				/>
			)}

			{uploadError && (
				<Typography color="error" data-testid="keyphrases-upload-error">
					{uploadError}
				</Typography>
			)}

			<AlertDialog
				title="Overwrite Existing Rows?"
				message={['Choosing to extend an existing Keyphrases List will overwrite any existing entries', 'Continue?']}
				isOpen={Boolean(queuedExtendList)}
				rightButtonAction={() => setQueuedExtendList(null)}
				rightButtonMessage={'No'}
				leftButtonAction={() => {
					setMenuAnchor(null);
					loadExistingKeyphraseList(queuedExtendList as KeyphrasesInfo);
					setLoadingKeyphrase(true);
					setUploadError('');
					setQueuedExtendList(null);
				}}
				leftButtonMessage={'Yes'}
			/>
		</Stack>
	);
};

export default CreateKeyphrases;
